import { useEffect, useRef } from "react";
import { Logo } from "@/components/logo";
import { useSettingsDialog } from "@/components/settings/settings-provider";
import { Button } from "@/components/ui/button";
import { useLicense } from "@/hooks/use-license";
import { adUnit, loadAdSense, requestAd, type Placement } from "@/lib/ads";
import { env } from "@/lib/env";
import { LICENSE_PRICE } from "@/lib/license";
import { cn } from "@/lib/utils";

/** Reserved size per placement, so nothing moves when the ad arrives. */
const SIZES: Record<Placement, string> = {
  landing: "h-[120px] w-full max-w-3xl",
  create: "h-[100px] w-full max-w-lg",
  room: "h-[250px] w-[300px]",
};

/**
 * One ad placement. Renders nothing when ads are switched off (VITE_ADS_ENABLED) or for license
 * holders. With AdSense configured it shows an
 * AdSense unit, and our own "remove ads" promotion when AdSense has nothing to show (see the
 * `data-house-ad` rules in styles.css); without AdSense, the promotion only.
 */
export function AdSlot({ placement, className }: { placement: Placement; className?: string }) {
  const license = useLicense();
  const { open } = useSettingsDialog();
  const unit = adUnit(placement);
  const ref = useRef<HTMLModElement>(null);

  const client = unit?.client;
  useEffect(() => {
    if (!env.adsEnabled || license || !client || !ref.current) return;
    loadAdSense(client);
    requestAd(ref.current);
  }, [license, client]);

  if (!env.adsEnabled || license) return null;

  return (
    <aside
      aria-label="Advertisement"
      data-testid={`ad-${placement}`}
      className={cn("flex flex-col gap-1", className)}
    >
      <div className="flex items-center justify-between text-[11px] text-muted-foreground">
        <span>{unit ? "Advertisement" : "Sponsored by us"}</span>
        <button
          type="button"
          onClick={open}
          className="cursor-pointer underline-offset-2 hover:text-foreground hover:underline"
        >
          Remove ads
        </button>
      </div>
      <div className={cn("relative overflow-hidden rounded-lg", SIZES[placement])}>
        {unit && (
          <ins
            ref={ref}
            className="adsbygoogle block h-full w-full"
            data-ad-client={unit.client}
            data-ad-slot={unit.slot}
            data-ad-format={placement === "room" ? undefined : "horizontal"}
          />
        )}
        <div
          data-house-ad
          className={cn(
            "h-full items-center justify-center gap-4 rounded-lg border border-dashed border-primary/30 bg-primary/5 px-4 text-center",
            placement === "room" ? "flex-col" : "flex-col sm:flex-row sm:text-left",
            unit ? "hidden" : "flex",
          )}
        >
          <Logo className="size-8" />
          <p className="text-sm">
            <span className="font-semibold">Enjoying Quick Planning Poker?</span>{" "}
            <span className="text-muted-foreground">
              Remove all ads for good: one payment, {LICENSE_PRICE}, no account.
            </span>
          </p>
          <Button size="sm" onClick={open} className="shrink-0">
            Remove ads
          </Button>
        </div>
      </div>
    </aside>
  );
}
